// Report User Modal Component
// Following existing patterns from other modals

import React, { useState } from 'react';
import { ReportType } from '../../types/safety';
import {
  ReportUserModal as StyledReportUserModal,
  ReportModalOverlay,
  ReportModalContent,
  ReportModalHeader,
  ReportModalTitle,
  ReportModalClose,
  ReportForm,
  ReportFormGroup,
  ReportLabel,
  ReportSelect,
  ReportTextarea,
  ReportFormActions,
  ReportButton,
  CancelButton
} from '../../styles/Safety';

interface ReportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReport: (reportType: ReportType, reason: string, description?: string) => Promise<void>;
  reportedUserName?: string;
}

const REPORT_TYPES: { value: ReportType; label: string }[] = [
  { value: 'inappropriate_content', label: 'Inappropriate Content' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'spam', label: 'Spam' },
  { value: 'fake_profile', label: 'Fake Profile' },
  { value: 'scam', label: 'Scam' },
  { value: 'underage', label: 'Underage' },
  { value: 'other', label: 'Other' }
];

export const ReportUserModal: React.FC<ReportUserModalProps> = ({
  isOpen,
  onClose,
  onReport,
  reportedUserName
}) => {
  const [reportType, setReportType] = useState<ReportType>('inappropriate_content');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      alert('Please provide a reason for reporting this user.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onReport(reportType, reason, description);
      // Reset form
      setReportType('inappropriate_content');
      setReason('');
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Error reporting user:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReportType('inappropriate_content');
      setReason('');
      setDescription('');
      onClose();
    }
  };

  return (
    <StyledReportUserModal>
      <ReportModalOverlay onClick={handleClose} />
      <ReportModalContent>
        <ReportModalHeader>
          <ReportModalTitle>
            Report {reportedUserName || 'User'}
          </ReportModalTitle>
          <ReportModalClose onClick={handleClose}>×</ReportModalClose>
        </ReportModalHeader>
        <ReportForm onSubmit={handleSubmit}>
          <ReportFormGroup>
            <ReportLabel htmlFor="reportType">Report Type</ReportLabel>
            <ReportSelect
              id="reportType"
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
              required
            >
              {REPORT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </ReportSelect>
          </ReportFormGroup>
          
          <ReportFormGroup>
            <ReportLabel htmlFor="reason">Reason *</ReportLabel>
            <ReportTextarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please describe why you're reporting this user..."
              required
            />
          </ReportFormGroup>
          
          <ReportFormGroup>
            <ReportLabel htmlFor="description">Additional Details (Optional)</ReportLabel>
            <ReportTextarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any additional information that might help us review this report..."
            />
          </ReportFormGroup>
          
          <ReportFormActions>
            <CancelButton type="button" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </CancelButton>
            <ReportButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </ReportButton>
          </ReportFormActions>
        </ReportForm>
      </ReportModalContent>
    </StyledReportUserModal>
  );
};






